import Card from "components/Cards/Card";
import EditCreateHeaderCard from "components/Cards/EditCreateHeaderCard";
import CreateFormFooter from "components/UI/atoms/CreateFormFooter";
import AssociatedListing from "components/UI/organisms/AssociatedListing";
import useForms from "helpers/hooks/useForms";
import {
  debounced,
  keyVal,
  payloadToArrayKeyValues,
} from "helpers/utils/utilities";
import DashboardLayout from "pages/layouts/dashboard";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useLabPrograms from "store/lab-programs/service-hook";
import useLabs from "store/labs/service-hook";
import { schema, stepSchema } from "./data";
import LabProgramDetails from "./LabProgramDetails";
import { toast } from "react-toastify";
import { COMPONENT_TYPES } from "helpers/utils/constants";
import useAssociation from "store/association";
import { useLocalizedTranslation } from "helpers/hooks/useLocalizedTranslation";
import AchievementStep from "components/UI/organisms/AchievementStep";
import { validateAchievements } from "components/UI/organisms/AddAchievementSection";
import { isTrue } from "helpers/utils/validators";
import { handleFetchLabsAssociateList, labProgramPayload } from "./utils";
import { handleSetSelectedAssociations } from "pages/labs/forms/helpers";

const STEPS = [
  "Lab Program Details",
  "Associated Labs",
  "Achievements (optional)",
];

const initData = {
  privacy: "no",
  is_achievement_enabled: false,
};

const CreateLabProgram = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [experts, setExperts] = useState([]);
  const { create, edit, fetchLP } = useLabPrograms();
  const { fetchLabsSelectList } = useLabs();
  const [labs, setLabs] = useState({ all: [], selected: [] });

  const { id } = useParams();
  const { fetchAssociation } = useAssociation();
  const editMode = !!id;
  const [initLoading, setInitLoading] = useState(Boolean(editMode));
  
  // RETURN VALIDATION SCHEMA FOR CURRENT STEP
  const sliceSchema = (_schema) => stepSchema(currentStep);
  const { t } = useLocalizedTranslation();

  const { data, errors, validate, onChange, setData } = useForms(
    schema,
    initData,
    sliceSchema
  );

  // RESET FORM DATA AND RELATED STATES
  const clear = () => {
    setData(initData);
    setCurrentStep(0);
    setLabs({ all: [], selected: [] });
    setExperts([]);
  };

  useEffect(() => {
    if (editMode) handleFetchDetail();
    else clear();
  }, [id, editMode]);

  useEffect(() => {
    if (currentStep === 1 && !labs?.all?.length) setAssociations();
  }, [editMode, currentStep]);

  // FETCH ASSOCIATED LABS AND ALL LABS LIST
  const setAssociations = async (skipAll) => {
    setLabs((prev) => ({ ...prev, loading: true }));
    let selected = [];
    if (editMode) {
      selected = await handleSetSelectedAssociations(
        fetchAssociation,
        COMPONENT_TYPES.LAB,
        COMPONENT_TYPES.LAB_PROGRAMS,
        id
      );
      if (skipAll) return selected;
    }
    const all = await handleFetchLabsAssociateList(null, fetchLabsSelectList);
    setLabs({ all, selected });
  };

  // SEARCH LABS WITH DEBOUNCED INPUT
  const onSearchLabs = async (search) =>
    handleFetchLabsAssociateList(setLabs, fetchLabsSelectList, search);

  const debouncedSearchLabs = debounced(onSearchLabs);

  // FETCH LAB PROGRAM DETAILS FOR EDIT MODE AND SET FORM DATA
  const handleFetchDetail = async () => {
    const res = await fetchLP(id);
    if (res) {
      const {
        title,
        description,
        skills = {},
        skill_groups = {},
        skill_stacks = {},
        category_id,
        level_id,
        duration_id,
        id: _id,
        media,
        achievement,
        labs,
        privacy,
        category,
        level,
        duration,
        type,
        mode,
        is_achievement_enabled,
        expert_references,
      } = res;

      setData({
        title,
        description,
        skills: payloadToArrayKeyValues(skills),
        skill_groups: payloadToArrayKeyValues(skill_groups),
        skill_stacks: payloadToArrayKeyValues(skill_stacks),
        category_id: keyVal(category, category_id),
        level_id: keyVal(level, level_id),
        duration_id: keyVal(duration, duration_id),
        is_achievement_enabled: isTrue(is_achievement_enabled),
        ...(isTrue(achievement?.is_pre_built_achievement)
          ? { pre_built_achievement_id: achievement?.achievement_id }
          : { ...achievement }),
        id: _id,
        media,
        labs,
        privacy,
        type,
        mode,
      });

      setExperts(expert_references || []);

      setInitLoading(false);
    }
  };

  // HANDLE SAVE OR EDIT OF LAB PROGRAM PAYLOAD
  const handleSaveOrEdit = async (isDraft) => {
    const payload = labProgramPayload({
      data,
      labs,
      experts,
      editMode,
      isDraft,
    });

    setLoading(true);
    editMode ? await edit(id, payload) : await create(payload);
    setLoading(false);
  };

  // HANDLE NEXT STEP LOGIC INCLUDING VALIDATION AND CONDITIONAL BEHAVIOR
  const handleNext = async (isDraft) => {
    if (currentStep === 1 && !labs.selected.length) {
      toast.error(t("Please select at least one lab!"));
      return;
    }
    if (currentStep === 2 && data?.is_achievement_enabled) {
      const achievementErrors = validateAchievements(data, false, t);
      if (achievementErrors) return;
    }
    const _errors = await validate();
    if (!_errors.size) {
      if (currentStep === 2) {
        handleSaveOrEdit(isDraft);
      } else {
        setCurrentStep((val) => val + 1);
      }
    }
  };

  const handleBack = () => {
    setCurrentStep((val) => val - 1);
  };

  return (
    <DashboardLayout loading={initLoading}>
      <EditCreateHeaderCard
        current={currentStep}
        length={STEPS.length}
        title={`${editMode ? "EDIT A" : "CREATE A"} LAB PROGRAM`}
        classes="mt-header"
        subtitles={STEPS}
      />
      <Card centered classes="my-3 column-center container">
        {currentStep === 0 && (
          <LabProgramDetails
            data={data}
            errors={errors}
            onChange={onChange}
            step={currentStep}
            experts={experts}
            setExperts={setExperts}
            t={t}
          />
        )}

        {currentStep === 1 && (
          <AssociatedListing
            onSearch={debouncedSearchLabs}
            list={labs}
            setList={setLabs}
            type={COMPONENT_TYPES.LAB}
            title={t("Browse Labs to add to your Lab Program")}
            subTitle={t(
              "You may only associate Labs with the same visibility as the Lab Program."
            )}
            component="Labs"
            tabs={["All"]}
            isLoading={labs?.loading}
          />
        )}
        {currentStep === 2 && (
          <AchievementStep
            data={data}
            component={COMPONENT_TYPES.LAB_PROGRAMS}
            errors={errors}
            onChange={onChange}
          />
        )}
        <CreateFormFooter
          step={currentStep}
          stepsLength={STEPS.length}
          onCancel={handleBack}
          disabled={!!errors.size}
          loading={loading}
          onOk={handleNext}
          editMode={editMode}
          okButtonTitle={
            editMode && currentStep === 2
              ? "Save & Exit"
              : !editMode && currentStep === 2
              ? "Create Lab Program"
              : "Continue"
          }
          okButtonColor="blue"
          backButtonColor="blue"
          confirmationOkBtnTitle="Exit Now"
        />
      </Card>
    </DashboardLayout>
  );
};

export default CreateLabProgram;